import { useState } from "react";
import { NavLink } from "react-router-dom";
import { UserMenu } from "@/components/UserMenu";
import { Lock, KeyRound, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePractice } from "@/pages/StaffApp";
import SetPinDialog from "@/components/SetPinDialog";
import { ManagePinDialog } from "@/components/ManagePinDialog";
import { ROLE_LABEL, NAV_COLORS } from "@/lib/status";

const NAV = [
  { to: "/", label: "Workspace", perm: ["packet:read"] },
  { to: "/schedule", label: "Schedule", perm: ["schedule:read"] },
  { to: "/templates", label: "Templates", perm: ["template:read"] },
  { to: "/imports", label: "Roster import", perm: ["import:run"] },
  { to: "/reports", label: "Reports", perm: ["report:read"] },
  { to: "/admin", label: "Admin", perm: ["member:manage", "settings:manage", "audit:read", "authz:manage"] },
];

export default function TopBar() {
  const { practice, me, role, canAny, switchPractice, lockNow, reloadMe, facility, setFacility, role_labels } = usePractice();
  const [pinOpen, setPinOpen] = useState(false);
  const [vpOpen, setVpOpen] = useState(false);
  const lock = () => (me.has_pin ? lockNow() : setPinOpen(true));
  const isDefault = me.default_org_id === practice.id;
  const makeDefault = async () => {
    try { await api.put("/me/default-org", { tenant_id: practice.id }); toast.success(`${practice.name} opens first when you sign in`); reloadMe(); } catch (e) { toast.error(errMsg(e)); }
  };
  const nav = [...NAV.filter((n) => canAny(...n.perm)), ...(me.super_admin ? [{ to: "/enterprise", label: "Enterprise" }] : [])];
  return (
    <header className="min-h-16 shrink-0 border-b bg-white/95 backdrop-blur-md shadow-sm flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1 px-3 py-2 lg:px-4 relative" data-testid="top-command-bar">
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: "linear-gradient(90deg,#4F46E5,#059669,#0284C7,#D97706,#C026D3,#E11D48)" }} />
      <div className="flex items-center gap-2 min-w-0 flex-1 md:flex-none">
        <img src="/ace-mark.png" alt="ACE eSIGN" title="ACE eSIGN" className="h-9 w-auto shrink-0 transition-transform hover:scale-105" data-testid="topbar-logo" />
        <Select value={practice.id} onValueChange={switchPractice}>
          <SelectTrigger className="h-10 border-0 shadow-none font-semibold max-w-[110px] sm:max-w-[260px] focus:ring-0 [&>span]:truncate" data-testid="practice-switcher" aria-label="Active organization">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {me.memberships.map((m) => (
              <SelectItem key={m.tenant_id} value={m.tenant_id} data-testid={`practice-option-${m.practice.short}`}>
                {m.practice.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {me.memberships.length > 1 && <button type="button" onClick={isDefault ? undefined : makeDefault} title={isDefault ? "Your default organization" : "Make this my default organization"}
          className="shrink-0 p-1 rounded hover:bg-slate-100 transition-colors" data-testid="default-org-btn" aria-pressed={isDefault}>
          <Star className={`w-4 h-4 ${isDefault ? "fill-amber-400 text-amber-500" : "text-slate-400"}`} /></button>}
        <Select value={facility || "all"} onValueChange={(v) => setFacility(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs w-auto max-w-[105px] sm:max-w-[220px] [&>span]:truncate" data-testid="facility-switcher" aria-label="Facility filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="facility-option-all">All facilities ({practice.facilities.length})</SelectItem>
            {practice.facilities.map((f) => <SelectItem key={f.code} value={f.code} data-testid={`facility-option-${f.code}`}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <nav className="order-last md:order-none w-full md:w-auto md:flex-1 flex items-center gap-1.5 overflow-x-auto py-1">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} style={{ "--tab": NAV_COLORS[n.to] }}
            className={({ isActive }) => `nav-tab px-3.5 py-2 rounded-lg text-sm whitespace-nowrap font-semibold transition-[background-color,color,box-shadow,transform] duration-150 ${isActive ? "nav-tab-active" : ""}`}>
            {n.label}
          </NavLink>
        ))}
      </nav>
      <span className="hidden md:inline text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid="synthetic-banner">Synthetic data only</span>
      {role === "provider" && <Button variant="outline" size="sm" className="h-10 hidden sm:inline-flex" onClick={() => setVpOpen(true)} data-testid="manage-verification-pin-btn" title="Manage Verification PIN"><KeyRound className="w-4 h-4 sm:mr-1" /><span className="hidden lg:inline">Verification PIN</span></Button>}
      <Button variant="outline" size="sm" className="h-10" onClick={lock} data-testid="lock-workstation-btn"><Lock className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Lock</span></Button>
      <UserMenu me={me} roleLabel={role_labels?.[role] || ROLE_LABEL[role] || role} showVersion={role === "practice_admin" || me?.platform_role === "platform_admin" || me?.super_admin} />
      <SetPinDialog open={pinOpen} onOpenChange={setPinOpen} onDone={() => { reloadMe(); }} />
      <ManagePinDialog open={vpOpen} onOpenChange={setVpOpen} />
    </header>
  );
}
